// Activation trend model: daily counts plus a 7-day moving average over a short window
// ending at the selected period's end. Days are bucketed in Europe/Copenhagen so the same
// data yields the same bars for every viewer, and so weekend colouring cannot drift by
// viewer location.

const TIME_ZONE = 'Europe/Copenhagen';

export const MOVING_AVERAGE_DAYS = 7;

// Fetched but never plotted, so the first plotted average is a true seven-day average.
export const TREND_WARM_UP_DAYS = MOVING_AVERAGE_DAYS - 1;

export const TREND_WINDOW_OPTIONS = [
    { days: 14, label: '2 uger', titleSuffix: 'seneste 2 uger' },
    { days: 30, label: '1 måned', titleSuffix: 'seneste måned' },
    { days: 60, label: '2 måneder', titleSuffix: 'seneste 2 måneder' },
    { days: 90, label: '3 måneder', titleSuffix: 'seneste 3 måneder' },
];

export const DEFAULT_TREND_WINDOW_DAYS = 60;

const MS_PER_DAY = 24 * 60 * 60 * 1000;

const dayKeyFormatter = new Intl.DateTimeFormat('en-CA', {
    timeZone: TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
});

const offsetPartsFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: TIME_ZONE,
    hour12: false,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
});

const pad = (value) => String(value).padStart(2, '0');

export const getDayKey = (timestamp) => dayKeyFormatter.format(new Date(timestamp));

// Day keys are shifted through UTC noon so a DST transition can never move a date.
const dayKeyToUtcNoon = (dayKey) => {
    const [year, month, day] = dayKey.split('-').map(Number);
    return Date.UTC(year, month - 1, day, 12);
};

const utcNoonToDayKey = (utcNoon) => {
    const date = new Date(utcNoon);
    return `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`;
};

export const shiftDayKey = (dayKey, days) => utcNoonToDayKey(dayKeyToUtcNoon(dayKey) + days * MS_PER_DAY);

export const isWeekendDayKey = (dayKey) => {
    const weekday = new Date(dayKeyToUtcNoon(dayKey)).getUTCDay();
    return weekday === 0 || weekday === 6;
};

export const isWeekStartDayKey = (dayKey) => new Date(dayKeyToUtcNoon(dayKey)).getUTCDay() === 1;

const getZoneOffsetMs = (timestamp) => {
    const parts = offsetPartsFormatter.formatToParts(new Date(timestamp)).reduce((acc, part) => {
        if (part.type !== 'literal') acc[part.type] = Number(part.value);
        return acc;
    }, {});

    const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour % 24, parts.minute, parts.second);
    return asUtc - Math.floor(timestamp / 1000) * 1000;
};

// Resolved in two passes so a day starting inside a DST transition still lands on midnight.
export const getDayStartUnix = (dayKey) => {
    const [year, month, day] = dayKey.split('-').map(Number);
    const utcMidnight = Date.UTC(year, month - 1, day);
    const firstPass = utcMidnight - getZoneOffsetMs(utcMidnight);
    return utcMidnight - getZoneOffsetMs(firstPass);
};

export const getDayEndUnix = (dayKey) => getDayStartUnix(shiftDayKey(dayKey, 1)) - 1;

const buildDayKeys = (endDayKey, dayCount) => (
    Array.from({ length: dayCount }, (_, index) => shiftDayKey(endDayKey, index - (dayCount - 1)))
);

// The window ends at the selected period's end, or today when the period is still running:
// a current year, month or week ends in the future, and anchoring there would place the
// whole window beyond the last record.
export const getTrendAnchorUnix = (periodEndUnix, nowUnix = Date.now()) => (
    Math.min(Number.isFinite(periodEndUnix) ? periodEndUnix : nowUnix, nowUnix)
);

// The span to query: the plotted window plus the warm-up days ahead of it.
export const getTrendFetchRange = (periodEndUnix, windowDays) => {
    const endDayKey = getDayKey(getTrendAnchorUnix(periodEndUnix));
    const windowStartDayKey = shiftDayKey(endDayKey, -(windowDays - 1));
    const fetchStartDayKey = shiftDayKey(windowStartDayKey, -TREND_WARM_UP_DAYS);

    return {
        fetchStartUnix: getDayStartUnix(fetchStartDayKey),
        fetchEndUnix: getDayEndUnix(endDayKey),
    };
};

// One entry per day of the window, oldest first. Warm-up days feed the average and are
// then dropped, so summing `count` over the result never includes them.
export const buildActivationTrend = (records, periodEndUnix, windowDays) => {
    const endDayKey = getDayKey(getTrendAnchorUnix(periodEndUnix));
    const dayKeys = buildDayKeys(endDayKey, windowDays + TREND_WARM_UP_DAYS);

    const countsByDayKey = new Map();
    (records || []).forEach((record) => {
        const timestamp = record?.timestamp;
        if (!Number.isFinite(timestamp)) return;

        const dayKey = getDayKey(timestamp);
        countsByDayKey.set(dayKey, (countsByDayKey.get(dayKey) || 0) + 1);
    });

    const counts = dayKeys.map((dayKey) => countsByDayKey.get(dayKey) || 0);

    return dayKeys.slice(TREND_WARM_UP_DAYS).map((dayKey, plottedIndex) => {
        const index = plottedIndex + TREND_WARM_UP_DAYS;
        const window = counts.slice(index - TREND_WARM_UP_DAYS, index + 1);
        const movingAverage = window.reduce((total, count) => total + count, 0) / MOVING_AVERAGE_DAYS;
        const [, month, day] = dayKey.split('-');

        return {
            dayKey,
            label: `${day}-${month}`,
            count: counts[index],
            movingAverage: Math.round(movingAverage * 10) / 10,
            isWeekend: isWeekendDayKey(dayKey),
            isWeekStart: isWeekStartDayKey(dayKey),
        };
    });
};
